// Impact-rating trade nudge — the live-engine port of the anchored Impact Rating oracle
// (research/src/impactRating.js), calibrated there against real 1947-2026 advanced stats.
//
// Purpose: ZenGM's player.value is PER-based, so it under-weights the two-way / glue impact
// that box scores miss (defense, on/off, low-usage efficiency). This module turns a player's
// OWN simulated advanced stats — dbpm/onOff100/usgp, which advStats.basketball.ts already
// computes and stores on every stats row each season — into a bounded multiplier on trade
// value. Glue guys (Caruso, Draymond) get a keep-premium; empty-volume scorers (LaVine) a
// discount. Neutral (1.0) when a player has no completed season yet (rookies, league start),
// so stock behavior and cold-start leagues are unchanged.
//
// This augments OVR; it does NOT replace it. Everything is gated by CUSTOM_TRADE_LOGIC at the
// call site and is basketball-only in effect (other sports lack these fields -> signal 0 -> 1.0).

// The subset of advanced stats the nudge reads (all written by advStats.basketball.ts).
export type ImpactSeasonStats = {
	min?: number;
	obpm?: number;
	dbpm?: number;
	onOff100?: number | null;
	usgp?: number;
	playoffs?: boolean;
};

export const IMPACT_CONFIG = {
	// Weights on the impact PER misses. Mirror the oracle's "def / onOff / usg" terms — the
	// components that separate glue guys from empty volume once box scoring is set aside.
	def: 0.7, // extra weight on DBPM (defense)
	onOff: 0.15, // per on/off net point per 100
	usg: 0.06, // inverse usage: low-usage impact rewarded, ball-dominance nudged down
	usgNeutral: 20, // usage% treated as neutral

	// On/off is null pre-play-by-play; in-sim it's always present, but impute from BPM if not.
	imputeSlope: 1.75,
	onOffCapLo: -12,
	onOffCapHi: 30,

	minFull: 2000, // minutes for full credibility; small samples shrink toward neutral
	tradeSwing: 0.2, // hard cap on the multiplier: value is nudged at most +/-20%
	signalScale: 6, // glue signal magnitude that saturates the swing (via tanh)

	// Negative-asset contracts. A max-money, multi-year, low-impact volume scorer (LaVine,
	// Morant) is worth LESS than nothing — in real life you attach a pick to move him. This is
	// a post-exponent liability (in contribution units, where a star ~= 2^7 = 128) that can
	// push a talented-but-overpaid player's trade value below zero. It is gated by a
	// "justification" score: a playoff riser or high/ascending-VORP player on the same max is
	// NOT a liability because his production earns the money.
	contract: {
		liabilityScale: 300, // overpay(=frac of cap) * yearsW * (1-just) * this -> contribution units
		yearsSpan: 2, // yearsLeft 1 -> ~0 liability (just let him expire), 3+ -> full
		vorpLow: 1.5, // reg-season VORP at/below which production justifies nothing
		vorpHigh: 6, // reg-season VORP at/above which the money is fully justified
		ascendBonus: 0.15, // extra justification if VORP rose year over year (ascending)
		playoffFull: 4, // playoff BPM that fully justifies the money ("plays well in the playoffs")
	},

	// On-court effectiveness (Phase 2). A player's PRIOR-season impact nudges this season's
	// on-court composite ratings — proven glue guys play a touch better, empty-volume guys a
	// touch worse. Deliberately small (comparable to home-court advantage) and non-circular
	// (prior completed season only, never the season being simulated).
	//
	// TODO (revisit later): the empty-volume PENALTY side is weak — the A/B harness
	// (GameSim.basketball/impactAB.test.ts) measured a Draymond-line BOOST at ~1.031 (+3.1%)
	// but a LaVine-line PENALTY at only ~0.993 (-0.7%), because glueSignal rewards elite glue
	// far more than it punishes empty volume. If we want overpaid volume guys to visibly fade,
	// scale up the negative side (e.g. an asymmetric swing, or a steeper penalty in glueSignal).
	onCourt: {
		swing: 0.04, // base +/-4% on composites from prior-season impact
		playoffSwing: 0.03, // additional +/-3% swing in the playoffs (riser/faller)
	},
};

// On-court impact is gated separately from trade logic so it can be toggled independently.
export const CUSTOM_ONCOURT_IMPACT = true;

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

// The "impact the box score misses", credibility-weighted. Positive for glue/defense/low-usage,
// negative for ball-dominant empty volume, ~0 for a neutral or statless player.
export const glueSignal = (s: ImpactSeasonStats, cfg = IMPACT_CONFIG): number => {
	const bpm = (s.obpm ?? 0) + (s.dbpm ?? 0);
	const onOffRaw = s.onOff100 == null ? cfg.imputeSlope * bpm : s.onOff100;
	const onOff = clamp(onOffRaw, cfg.onOffCapLo, cfg.onOffCapHi);
	const cred = clamp((s.min ?? 0) / cfg.minFull, 0, 1);

	return (
		cred *
		(cfg.def * (s.dbpm ?? 0) +
			cfg.onOff * onOff +
			cfg.usg * (cfg.usgNeutral - (s.usgp ?? cfg.usgNeutral)))
	);
};

// Bounded trade-value multiplier in [1 - tradeSwing, 1 + tradeSwing]. Applied post-exponent to
// a player's contribution so it's a +/-tradeSwing nudge on final value, not an exploded one.
export const impactTradeFactor = (
	s: ImpactSeasonStats | undefined,
	cfg = IMPACT_CONFIG,
): number => {
	// Cold start: no completed season / negligible minutes -> neutral, behavior unchanged.
	if (!s || (s.min ?? 0) < 1) {
		return 1;
	}
	return 1 + cfg.tradeSwing * Math.tanh(glueSignal(s, cfg) / cfg.signalScale);
};

// Most recent COMPLETED (or in-progress) regular-season stats row — the one advStats keeps
// current. Playoffs excluded so the trade nudge tracks the full body of work, not a hot series.
export const latestImpactStats = (
	stats: any[] | undefined,
): ImpactSeasonStats | undefined => {
	if (!stats || stats.length === 0) {
		return undefined;
	}
	for (let i = stats.length - 1; i >= 0; i--) {
		const s = stats[i];
		if (s && !s.playoffs && (s.min ?? 0) > 0) {
			return s;
		}
	}
	return undefined;
};

// Convenience: factor straight from a DB player object. Uses the player's own most recent
// completed (simulated) season when available; otherwise falls back to `seed` — a real
// prior-season line looked up by srID (see realImpactSeeds.ts) so a real player is valued on
// his ACTUAL previous stats at league start, not neutrally. 1.0 if neither is available.
// Which stats to value a player on: his own once he has a completed (simulated) season,
// otherwise the real prior-season seed (a synthetic stats array; see realImpactSeeds.ts). Both
// impactFactor and moneyJustification read the result, so season-1 real players are valued on
// their actual production, not neutrally.
export const effectiveStats = (
	playerStats: any[] | undefined,
	seed: any[] | undefined,
): any[] | undefined => (latestImpactStats(playerStats) ? playerStats : seed);

// Impact trade factor straight from a stats array (own or seeded). 1.0 if none usable.
export const impactFactorFromStats = (stats: any[] | undefined): number =>
	impactTradeFactor(latestImpactStats(stats));

// The player's most recent COMPLETED regular season (strictly before `currentSeason`) — the
// basis for on-court effectiveness. Excludes the in-progress season so a player's current-year
// play never feeds back into his current-year games (non-circular).
export const priorSeasonRow = (
	stats: any[] | undefined,
	currentSeason: number,
): any | undefined => {
	if (!stats) {
		return undefined;
	}
	for (let i = stats.length - 1; i >= 0; i--) {
		const s = stats[i];
		if (
			s &&
			!s.playoffs &&
			(s.min ?? 0) > 0 &&
			s.season !== undefined &&
			s.season < currentSeason
		) {
			return s;
		}
	}
	return undefined;
};

// On-court effectiveness multiplier for a player's composite ratings, from his prior-season
// impact. Small and bounded (see IMPACT_CONFIG.onCourt): proven glue/defense guys get a slight
// boost, empty-volume guys a slight penalty, amplified in the playoffs (riser/faller). 1.0 when
// there's no completed prior season (rookies, league start) — they play to their ratings.
export const onCourtImpactFactor = (
	priorRow: ImpactSeasonStats | undefined,
	playoffs: boolean,
	cfg = IMPACT_CONFIG,
): number => {
	if (!priorRow || (priorRow.min ?? 0) < 1) {
		return 1;
	}
	const t = Math.tanh(glueSignal(priorRow, cfg) / cfg.signalScale);
	let factor = 1 + cfg.onCourt.swing * t;
	if (playoffs) {
		factor *= 1 + cfg.onCourt.playoffSwing * t;
	}
	return factor;
};

// Apply an on-court effectiveness factor to a player's composite ratings in place. Mirrors
// GameSim's homeCourtAdvantage: negative ratings (turnovers, fouling) are inverted so a boost
// REDUCES them, and endurance is left alone. No-op at exactly 1.0.
export const scaleCompositesByImpact = (
	compositeRating: Record<string, number>,
	factor: number,
): void => {
	if (factor === 1) {
		return;
	}
	for (const r of Object.keys(compositeRating)) {
		if (r === "endurance") {
			continue;
		}
		const v = compositeRating[r]!;
		// turnovers / fouling are negative ratings, so a boost should REDUCE them.
		compositeRating[r] = r === "turnovers" || r === "fouling" ? v / factor : v * factor;
	}
};

// The two most recent regular-season rows (latest first) and the most recent playoff row —
// but only if that playoff run is RECENT (within a season of his latest regular season). A
// years-old playoff run shouldn't justify a player's current contract (calibrated against
// Simmons' worst-contracts list, where stale runs were over-exempting declining guys).
const recentRows = (stats: any[] | undefined) => {
	const reg: any[] = [];
	let playoff: any | undefined;
	if (stats) {
		for (let i = stats.length - 1; i >= 0; i--) {
			const s = stats[i];
			if (!s) {
				continue;
			}
			if (s.playoffs) {
				if (!playoff && (s.min ?? 0) > 0) {
					playoff = s;
				}
			} else if ((s.min ?? 0) > 0 && reg.length < 2) {
				reg.push(s);
			}
		}
	}
	// Drop a stale playoff row (older than the latest regular season by more than a year).
	if (
		playoff &&
		reg[0] &&
		playoff.season !== undefined &&
		reg[0].season !== undefined &&
		playoff.season < reg[0].season - 1
	) {
		playoff = undefined;
	}
	return { reg, playoff };
};

// "Does his production justify the money?" in [0,1]. 1 = a max is fair; 0 = pure liability.
// Earned by a high (or ascending) regular-season VORP, or by playing well in the playoffs.
// `stats` is the effective stats (own or seeded — see effectiveStats).
export const moneyJustification = (
	stats: any[] | undefined,
	cfg = IMPACT_CONFIG,
): number => {
	const c = cfg.contract;
	const { reg, playoff } = recentRows(stats);
	const latest = reg[0];
	if (!latest) {
		// No production data at all — don't manufacture a liability out of nothing.
		return 1;
	}

	const regVorp = latest.vorp ?? 0;
	const level = clamp((regVorp - c.vorpLow) / (c.vorpHigh - c.vorpLow), 0, 1);

	const prevVorp = reg[1]?.vorp;
	const ascending =
		prevVorp !== undefined && regVorp > prevVorp ? c.ascendBonus : 0;

	const playoffBpm = playoff ? (playoff.obpm ?? 0) + (playoff.dbpm ?? 0) : 0;
	const playoffJust = clamp(playoffBpm / c.playoffFull, 0, 1);

	return clamp(Math.max(level, playoffJust) + ascending, 0, 1);
};

// Post-exponent trade-value liability (in contribution units) for a bad contract. Zero unless
// the player is actually overpaid; grows with the overpay, with locked-in years remaining, and
// with how little his production justifies the money. This is what can make an overpaid,
// multi-year, low-impact volume scorer a NEGATIVE asset (attach-a-pick-to-move-him).
export const contractLiability = (
	{
		overpay,
		yearsLeft,
		justification,
	}: { overpay: number; yearsLeft: number; justification: number },
	cfg = IMPACT_CONFIG,
): number => {
	if (overpay <= 0) {
		return 0;
	}
	const c = cfg.contract;
	const yearsW = clamp((yearsLeft - 1) / c.yearsSpan, 0, 1); // expiring ~0; 2-3 yrs ramps to 1
	return c.liabilityScale * overpay * yearsW * (1 - clamp(justification, 0, 1));
};
